// Notices which store you're in and switches the list to it.
//  • Browsers: GPS (with permission). Checked when the app comes to the front and every couple of
//    minutes while it's open — never in the background.
//  • A native shell can add Wi-Fi network names and Bluetooth beacons (window.MilkMaybe.handle({type: 'presence', …})).
// A store you pick by hand wins for 30 minutes, so the app doesn't fight you.

import { detectStore, type PresenceCues } from '@milk/shared';
import { getState, setStoreView, subscribe, toast } from '../store/store';
import { pref, setPref } from './config';
import { onNativeCommand, postNative } from './native';

const MANUAL_HOLD_MS = 30 * 60_000;
const GPS_EVERY_MS = 2 * 60_000;

let cues: PresenceCues = {};
let manualAt = 0;
let lastAuto: string | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let started = false;
let unsubscribe: (() => void) | null = null;
/** Which stores have locations (and Wi-Fi / beacons): when that changes — e.g. the first download on a new device — look again. */
let cueSignature = '';
const signature = () => Object.values(getState().stores).map((s) => `${s.id}:${s.location ? `${s.location.lat},${s.location.lng},${s.location.radiusM}` : ''}:${s.wifiNames.join('|')}:${s.beacons.join('|')}`).sort().join(';');

export const autoSwitchEnabled = () => pref('autoStore', true);
export function setAutoSwitch(on: boolean) {
  setPref('autoStore', on);
  if (on) { startPresence(); void checkGps(true); } else stopPresence();
}

/** The person picked a store themselves. */
export function noteManualChoice() {
  manualAt = Date.now();
}

function evaluate() {
  const s = getState();
  if (s.status !== 'ready' || !autoSwitchEnabled()) return;
  const hit = detectStore(Object.values(s.stores), cues);
  if (!hit) { lastAuto = null; return; }
  if (hit.store.id === s.storeId) { lastAuto = hit.store.id; return; }
  if (Date.now() - manualAt < MANUAL_HOLD_MS || hit.store.id === lastAuto) return;
  lastAuto = hit.store.id;
  setStoreView(hit.store.id);
  toast(`You’re at ${hit.store.name} — showing its aisle order`);
}

export async function gpsPermission(): Promise<PermissionState | 'unsupported'> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return 'unsupported';
  try { return (await navigator.permissions.query({ name: 'geolocation' as PermissionName })).state; } catch { return 'prompt'; }
}

/** One GPS fix (asks for permission if `ask`; `fresh` skips a fix from the last minute). Resolves null when unavailable or refused. */
export function currentPosition(ask = false, fresh = false): Promise<GeolocationPosition | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    void gpsPermission().then((p) => {
      if (p !== 'granted' && !ask) return resolve(null);
      navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), { enableHighAccuracy: true, maximumAge: fresh ? 0 : 60_000, timeout: 15_000 });
    });
  });
}

async function checkGps(ask = false) {
  if (document.visibilityState === 'hidden') return;
  if (!Object.values(getState().stores).some((s) => s.location)) return;
  const pos = await currentPosition(ask);
  if (!pos) return;
  cues = { ...cues, position: { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracyM: pos.coords.accuracy } };
  evaluate();
}

export function startPresence() {
  if (started || !autoSwitchEnabled()) return;
  started = true;
  void checkGps();
  timer = setInterval(() => void checkGps(), GPS_EVERY_MS);
  document.addEventListener('visibilitychange', onVisible);
  cueSignature = signature();
  tellNative();
  unsubscribe = subscribe(() => {
    const sig = signature();
    if (sig === cueSignature) return;
    cueSignature = sig;
    tellNative();
    void checkGps();
  });
}

/** Native shells: which Wi-Fi names and beacons belong to which store. */
function tellNative() {
  const stores = Object.values(getState().stores);
  postNative({ type: 'watchPresence', stores: stores.map((s) => ({ id: s.id, wifiNames: s.wifiNames, beacons: s.beacons })) });
}

export function stopPresence() {
  started = false;
  unsubscribe?.();
  unsubscribe = null;
  if (timer) clearInterval(timer);
  timer = null;
  document.removeEventListener('visibilitychange', onVisible);
  postNative({ type: 'watchPresence', stores: [] });
}

function onVisible() {
  if (document.visibilityState === 'visible') void checkGps();
}

if (typeof window !== 'undefined') {
  onNativeCommand((c) => {
    if (c.type !== 'presence') return;
    cues = { ...cues, wifiNames: c.wifiNames ?? [], beacons: c.beacons ?? [] };
    evaluate();
  });
}

/** For tests: feed cues directly. */
export function _setCues(c: PresenceCues) {
  cues = c;
  evaluate();
}
